// useWakeLock — keeps the device screen awake while the user is in the
// middle of a practice. Bilateral sessions are audio-heavy, hands-free
// friendly, and can run for many minutes with only occasional taps, so
// the OS is very quick to dim/lock the screen. That kills the vibe and
// can also suspend background audio on mobile.
//
// Two paths:
//   • Web (desktop + mobile Safari + Android) — `navigator.wakeLock.request('screen')`.
//     If the tab is hidden, browsers auto-release the sentinel; we listen
//     to visibilitychange and re-acquire when the user comes back.
//   • Native iOS (Capacitor) — the Web API works in WKWebView but is a bit
//     flaky mid-audio; we also call the community `KeepAwake` plugin so
//     UIApplication.isIdleTimerDisabled is set at the app level. This is
//     the same plugin Xcode-built apps use and it survives short lock
//     screens better than the pure Web API.
//
// Never throws — Wake Lock is a nice-to-have. Unsupported browsers
// (older Firefox, Safari <16.4) and permission failures fall through
// silently.
//
// Usage:
//   useWakeLock(shouldKeepAwake);   // pass a boolean
// Acquires when `shouldKeepAwake` flips true, releases when it flips
// false, and cleans up on unmount.

import { useEffect, useRef } from "react";
import { isNativeIOS } from "./platform";
import logger from "./logger";

// Lazy import — the plugin is only meaningful on native iOS. Bundlers
// tree-shake this fine; on web it's a tiny stub.
let KeepAwake = null;
try {
  // eslint-disable-next-line global-require
  KeepAwake = require("@capacitor-community/keep-awake").KeepAwake;
} catch (_e) {
  KeepAwake = null;
}

export function useWakeLock(shouldKeepAwake) {
  const sentinelRef = useRef(null);
  const nativeActiveRef = useRef(false);

  useEffect(() => {
    if (!shouldKeepAwake) return undefined;

    let cancelled = false;

    const acquireNative = async () => {
      if (!isNativeIOS() || !KeepAwake || nativeActiveRef.current) return;
      try {
        await KeepAwake.keepAwake();
        nativeActiveRef.current = true;
      } catch (err) {
        logger.warn("[wakeLock] native keep-awake failed", err?.message || err);
      }
    };

    const releaseNative = async () => {
      if (!nativeActiveRef.current || !KeepAwake) return;
      try {
        await KeepAwake.allowSleep();
      } catch (_e) {
        // ignore — releasing is best-effort
      }
      nativeActiveRef.current = false;
    };

    const acquireWeb = async () => {
      if (typeof navigator === "undefined" || !navigator.wakeLock) return;
      if (sentinelRef.current) return;
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await sentinel.release(); } catch (_e) { /* noop */ }
          return;
        }
        sentinelRef.current = sentinel;
        // The browser auto-releases when the tab hides; clear our ref so
        // the visibility handler can re-acquire on return.
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } catch (err) {
        // NotAllowedError shows up on some mobile Safari versions when
        // there's no active user gesture yet — that's fine, we'll retry
        // on the next visibilitychange.
        logger.warn("[wakeLock] web wakeLock failed", err?.message || err);
      }
    };

    const releaseWeb = async () => {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      if (!sentinel) return;
      try { await sentinel.release(); } catch (_e) { /* noop */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Re-acquire — browser released the sentinel when we hid.
        acquireWeb();
      }
    };

    acquireWeb();
    acquireNative();
    document.addEventListener("visibilitychange", onVisibility);

    // Some browsers require a user gesture to grant wake-lock the first
    // time (e.g. headless / cold-open Safari). If the initial acquire
    // was denied, the next tap on the page will retry — capture phase
    // + passive so we don't interfere with the app's own tap handlers.
    const onGesture = () => {
      if (!sentinelRef.current) acquireWeb();
    };
    document.addEventListener("pointerdown", onGesture, { passive: true, capture: true });

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("pointerdown", onGesture, { capture: true });
      releaseWeb();
      releaseNative();
    };
  }, [shouldKeepAwake]);
}

export default useWakeLock;
